import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, GlobalStyles, useTheme } from '@mui/material';
import { BrandMark, BrandLockup } from '../Brand/Logo';
import { apiGetStats } from '../../services/api';
import { authTokens, deskMesh, NOISE_BG, FONT_BODY, FONT_DISPLAY, FONT_MONO } from './brandTokens';

const TICKER =
  '#cs101  ✦  housing board  ✦  #intramural-soccer  ✦  professor reviews  ✦  #late-night-eats  ✦  club fair  ✦  #orgo-help  ✦  roommate finder  ✦  ride share  ✦  ';

// Honest fallback figures (rounded down, hence the "+") shown until the live /stats
// counts arrive. Vanity metrics (fake student/campus counts) were removed — courses &
// professors are the genuinely large, verifiable numbers.
const STATIC_STATS = [
  { n: '4,500+', l: 'Courses mapped' },
  { n: '2,300+', l: 'Professors listed' },
  { n: '0', l: 'Bots, ever' },
];

// Round a real count down to a clean "+" figure so it reads as honest social proof
// without churning on every new row (e.g. 4,521 -> "4,500+").
function roundedPlus(n: number): string {
  if (n >= 1000) return `${(Math.floor(n / 100) * 100).toLocaleString()}+`;
  if (n >= 100) return `${Math.floor(n / 10) * 10}+`;
  return String(n);
}

const CropMark: React.FC<{ pos: React.CSSProperties; color: string }> = ({ pos, color }) => (
  <svg width="11" height="11" viewBox="0 0 11 11" style={{ position: 'fixed', zIndex: 3, ...pos }}>
    <path d="M5.5 0V11M0 5.5H11" stroke={color} strokeWidth="1" />
  </svg>
);

/**
 * Editorial auth layout from the Claude Design handoff: ink/ember mesh background with grain,
 * a hairline keyline frame + corner crop marks, a masthead, a left "cover" (headline, verified
 * callout, stats), a bottom ticker, and a right-hand form panel (`children`). Responsive: the
 * cover detail collapses on small screens; follows the app's light/dark mode.
 */
const AuthScreen: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const mode = useTheme().palette.mode === 'dark' ? 'dark' : 'light';
  const t = authTokens(mode);
  const mono = { fontFamily: FONT_MONO, textTransform: 'uppercase' as const };

  const [stats, setStats] = useState(STATIC_STATS);
  useEffect(() => {
    let alive = true;
    apiGetStats()
      .then((s) => {
        if (!alive || !s?.courses) return; // keep the static fallback on empty/failed loads
        setStats([
          { n: roundedPlus(s.courses), l: 'Courses mapped' },
          { n: roundedPlus(s.professors), l: 'Professors listed' },
          { n: '0', l: 'Bots, ever' },
        ]);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        background: deskMesh(t),
        color: t.text,
        fontFamily: FONT_BODY,
        overflow: 'hidden',
      }}
    >
      <GlobalStyles
        styles={{
          '@keyframes dfFlick': { '0%,100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.84, transform: 'scale(0.95)' } },
          '@keyframes dfTick': { from: { transform: 'translateX(0)' }, to: { transform: 'translateX(-50%)' } },
          '.df-noscroll::-webkit-scrollbar': { width: 0, height: 0 },
        }}
      />

      {/* grain */}
      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: NOISE_BG, backgroundSize: '160px 160px', opacity: t.noiseOpacity, mixBlendMode: t.noiseBlend, zIndex: 1 }} />

      {/* keyline frame + corner crop marks + margin labels — desktop only */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box sx={{ position: 'fixed', inset: '22px', border: `1px solid ${t.line}`, borderRadius: '2px', pointerEvents: 'none', zIndex: 2 }} />
        <CropMark pos={{ left: 17, top: 17 }} color={t.ember} />
        <CropMark pos={{ right: 17, top: 17 }} color={t.ember} />
        <CropMark pos={{ left: 17, bottom: 17 }} color={t.ember} />
        <CropMark pos={{ right: 17, bottom: 17 }} color={t.ember} />
        <Box sx={{ position: 'fixed', left: 34, bottom: 30, zIndex: 3, ...mono, fontSize: 10, letterSpacing: '0.14em', color: t.muted }}>Fig. 01 — Access</Box>
        <Box sx={{ position: 'fixed', right: 34, bottom: 30, zIndex: 3, ...mono, fontSize: 10, letterSpacing: '0.14em', color: t.muted }}>Est. MMXXVI</Box>
      </Box>

      {/* content */}
      <Box sx={{ position: 'relative', zIndex: 4, minHeight: '100vh', maxWidth: 1240, mx: 'auto', px: { xs: 3, md: 7 }, py: { xs: 4, md: 6 }, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        {/* masthead */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box component={RouterLink} to="/login" aria-label="Go to sign in" sx={{ textDecoration: 'none', color: 'inherit', display: 'inline-flex' }}>
            <BrandLockup size={22} textColor={t.text} ember={t.ember} flicker />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, ...mono, fontSize: 10.5, letterSpacing: '0.16em', color: t.muted }}>Verified · Students only</Box>
            <Box sx={{ ...mono, fontSize: 10, letterSpacing: '0.12em', color: t.text, border: `1px solid ${t.line}`, borderRadius: 999, px: 1.4, py: '5px' }}>{mode === 'dark' ? 'Dark' : 'Light'}</Box>
          </Box>
        </Box>

        {/* spread */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, gap: { xs: 4, md: '68px' }, py: { xs: 4, md: 0 }, minHeight: 0 }}>
          {/* cover */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, ...mono, fontSize: 11, letterSpacing: '0.18em', color: t.ember }}>
              <BrandMark size={12} color={t.ember} /> A real campus, online
            </Box>
            <Box component="h1" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: { xs: 38, md: 66 }, lineHeight: 0.96, letterSpacing: '-0.03em', m: 0, color: t.text }}>
              Your campus,<br /><Box component="span" sx={{ color: t.ember }}>framed.</Box>
            </Box>
            <Box component="p" sx={{ display: { xs: 'none', md: 'block' }, m: 0, maxWidth: 430, fontSize: 16.5, lineHeight: 1.55, color: t.muted }}>
              Your classes, clubs, course channels, roommates and professor reviews — scoped to your school. No outsiders. No bots. No creeps.
            </Box>

            {/* verified callout — desktop */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.6, alignItems: 'flex-start', mt: '2px' }}>
              <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: 999, background: t.emberSoft, border: `1px solid ${t.line}`, display: 'grid', placeItems: 'center' }}>
                <BrandMark size={19} color={t.ember} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <Box sx={{ ...mono, fontSize: 11, letterSpacing: '0.13em', color: t.ember, fontWeight: 700 }}>Every member verified</Box>
                <Box sx={{ fontSize: 14, color: t.muted, lineHeight: 1.5, maxWidth: 380 }}>We check your .edu before you get in. Your campus stays yours.</Box>
              </Box>
            </Box>

            {/* verified pill — mobile */}
            <Box sx={{ display: { xs: 'inline-flex', md: 'none' }, alignSelf: 'flex-start', alignItems: 'center', gap: 0.9, px: 1.5, py: 0.75, borderRadius: 999, background: t.emberSoft, color: t.ember, fontSize: 12.5, fontWeight: 600 }}>
              <BrandMark size={12} color={t.ember} /> Verified students only
            </Box>

            {/* stats — desktop */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, borderTop: `1px solid ${t.line}`, mt: '6px' }}>
              {stats.map((s, i) => (
                <Box key={s.l} sx={{ flex: 1, pt: 2, pr: i < 2 ? '18px' : 0, pl: i > 0 ? '18px' : 0, borderRight: i < 2 ? `1px solid ${t.line}` : 'none' }}>
                  <Box sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 25, letterSpacing: '-0.02em', color: t.text }}>{s.n}</Box>
                  <Box sx={{ ...mono, fontSize: 10, letterSpacing: '0.1em', color: t.muted, mt: '3px' }}>{s.l}</Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* form panel */}
          <Box sx={{ width: { xs: '100%', md: 430 }, flexShrink: 0, boxSizing: 'border-box', p: { xs: '26px', md: '38px' }, borderRadius: '6px', background: t.panelBg, border: `1px solid ${t.line}`, boxShadow: t.panelShadow }}>
            {children}
          </Box>
        </Box>

        {/* ticker */}
        <Box sx={{ overflow: 'hidden', borderTop: `1px solid ${t.line}`, pt: 1.75, WebkitMaskImage: 'linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)', maskImage: 'linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)' }}>
          <Box sx={{ display: 'flex', width: 'max-content', animation: 'dfTick 34s linear infinite', fontFamily: FONT_MONO, fontSize: 11.5, letterSpacing: '0.06em', color: t.muted }}>
            <Box component="span" sx={{ whiteSpace: 'pre' }}>{TICKER}</Box>
            <Box component="span" aria-hidden sx={{ whiteSpace: 'pre' }}>{TICKER}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthScreen;
